import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Briefcase } from "lucide-react";

const EMPTY = {
  title:"", department:"", location:"", domain:"", experience_category:"Fresher",
  experience_min:0, experience_max:3,
  employment_type:"Full-time", salary_range:"", openings:1, about:"",
  responsibilities:[], required_qualifications:[], preferred_qualifications:[],
  required_skills:[], preferred_skills:[], benefits:[],
  status:"published", assessment_required:true,
  assessment_duration_min:20, assessment_questions_count:10, passing_score:60
};

export default function AdminJobs() {
  const [rows, setRows] = useState([]);
  const [depts, setDepts] = useState([]);
  const [locs, setLocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const load = React.useCallback(()=>api.get("/admin/jobs").then(r=>setRows(r.data)),[]);
  useEffect(()=>{
    load();
    api.get("/departments").then(r=>setDepts(r.data));
    api.get("/locations").then(r=>setLocs(r.data));
  },[load]);
  const asList = (s) => s.split(",").map(x=>x.trim()).filter(Boolean);
  const save = async () => {
    try {
      const payload = { ...f,
        responsibilities: Array.isArray(f.responsibilities) ? f.responsibilities : asList(f.responsibilities||""),
        required_qualifications: Array.isArray(f.required_qualifications) ? f.required_qualifications : asList(f.required_qualifications||""),
        preferred_qualifications: Array.isArray(f.preferred_qualifications) ? f.preferred_qualifications : asList(f.preferred_qualifications||""),
        required_skills: Array.isArray(f.required_skills) ? f.required_skills : asList(f.required_skills||""),
        preferred_skills: Array.isArray(f.preferred_skills) ? f.preferred_skills : asList(f.preferred_skills||""),
        benefits: Array.isArray(f.benefits) ? f.benefits : asList(f.benefits||""),
      };
      await api.post("/admin/jobs", payload);
      toast.success("Job created");
      setOpen(false); setF(EMPTY); load();
    } catch(e){ toast.error(e.response?.data?.detail || "Failed"); }
  };
  const listInput = (label, key, placeholder) => (
    <div>
      <Label>{label}</Label>
      <Input value={Array.isArray(f[key]) ? f[key].join(", ") : (f[key]||"")} onChange={e=>setF({...f, [key]: e.target.value})} placeholder={placeholder} className="bg-slate-800 border-slate-700 text-white"/>
    </div>
  );

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#F59E0B]">Jobs</div>
          <h1 className="font-display text-3xl font-bold text-white mt-1">Job Openings</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#F59E0B] text-[#0A2540] hover:bg-[#F59E0B]/90 font-semibold" data-testid="admin-create-job-btn"><Plus className="w-4 h-4 mr-2"/>Create Job</Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Create a new job</DialogTitle></DialogHeader>
            <div className="grid md:grid-cols-2 gap-3">
              <div><Label>Title</Label><Input value={f.title} onChange={e=>setF({...f,title:e.target.value})} className="bg-slate-800 border-slate-700 text-white" data-testid="job-form-title"/></div>
              <div><Label>Domain</Label>
                <Select value={f.domain} onValueChange={v=>setF({...f, domain:v})}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="job-form-domain"><SelectValue placeholder="Select domain"/></SelectTrigger>
                  <SelectContent>{["Java","Python","AI/ML","Data Analytics","Data Science","MERN","DevOps","Cloud","QA/Testing"].map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Experience Category</Label>
                <Select value={f.experience_category} onValueChange={v=>setF({...f, experience_category:v})}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="job-form-exp-cat"><SelectValue/></SelectTrigger>
                  <SelectContent>{["Fresher","0-1 Years","1-3 Years","3-5 Years","5+ Years"].map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Employment type</Label>
                <Select value={f.employment_type} onValueChange={v=>setF({...f, employment_type:v})}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white"><SelectValue/></SelectTrigger>
                  <SelectContent>{["Full-time","Part-time","Contract","Internship"].map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Department</Label>
                <Select value={f.department} onValueChange={v=>setF({...f,department:v})}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="job-form-dept"><SelectValue placeholder="Select"/></SelectTrigger>
                  <SelectContent>{depts.map(d=><SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Location</Label>
                <Select value={f.location} onValueChange={v=>setF({...f,location:v})}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="job-form-location"><SelectValue placeholder="Select"/></SelectTrigger>
                  <SelectContent>{locs.map(l=><SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Min experience (years)</Label><Input type="number" value={f.experience_min} onChange={e=>setF({...f,experience_min:Number(e.target.value)})} className="bg-slate-800 border-slate-700 text-white"/></div>
              <div><Label>Max experience (years)</Label><Input type="number" value={f.experience_max} onChange={e=>setF({...f,experience_max:Number(e.target.value)})} className="bg-slate-800 border-slate-700 text-white"/></div>
              <div><Label>Salary range</Label><Input value={f.salary_range} onChange={e=>setF({...f,salary_range:e.target.value})} placeholder="₹8-14 LPA" className="bg-slate-800 border-slate-700 text-white"/></div>
              <div><Label>Openings</Label><Input type="number" value={f.openings} onChange={e=>setF({...f,openings:Number(e.target.value)})} className="bg-slate-800 border-slate-700 text-white"/></div>
              <div className="md:col-span-2"><Label>About the role</Label><Textarea rows={3} value={f.about} onChange={e=>setF({...f,about:e.target.value})} className="bg-slate-800 border-slate-700 text-white"/></div>
              <div className="md:col-span-2">{listInput("Responsibilities (comma separated)","responsibilities","Build APIs, Code reviews...")}</div>
              <div>{listInput("Required qualifications","required_qualifications","B.Tech, 2+ yrs...")}</div>
              <div>{listInput("Preferred qualifications","preferred_qualifications","Docker...")}</div>
              <div>{listInput("Required skills","required_skills","Python, FastAPI, SQL")}</div>
              <div>{listInput("Preferred skills","preferred_skills","Docker, AWS")}</div>
              <div className="md:col-span-2">{listInput("Benefits","benefits","Health insurance, Learning budget")}</div>
              <div><Label>Assessment duration (min)</Label><Input type="number" value={f.assessment_duration_min} onChange={e=>setF({...f,assessment_duration_min:Number(e.target.value)})} className="bg-slate-800 border-slate-700 text-white"/></div>
              <div><Label>Questions count</Label><Input type="number" value={f.assessment_questions_count} onChange={e=>setF({...f,assessment_questions_count:Number(e.target.value)})} className="bg-slate-800 border-slate-700 text-white"/></div>
              <div><Label>Passing score %</Label><Input type="number" value={f.passing_score} onChange={e=>setF({...f,passing_score:Number(e.target.value)})} className="bg-slate-800 border-slate-700 text-white"/></div>
              <div className="flex items-center gap-3 mt-6"><Switch checked={f.assessment_required} onCheckedChange={v=>setF({...f,assessment_required:v})}/><Label>Assessment required</Label></div>
            </div>
            <DialogFooter><Button onClick={save} className="bg-[#F59E0B] text-[#0A2540] hover:bg-[#F59E0B]/90 font-semibold" data-testid="job-form-save">Publish job</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-slate-900 border-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-950 text-slate-400 text-xs uppercase tracking-wider">
              <tr><th className="text-left px-4 py-3">Title</th><th className="text-left px-4 py-3">Department</th><th className="text-left px-4 py-3">Location</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Skills</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {rows.map(j=>(
                <tr key={j.id} className="hover:bg-slate-800/50" data-testid={`admin-job-row-${j.id}`}>
                  <td className="px-4 py-3"><div className="font-medium text-white">{j.title}</div><div className="font-mono text-[11px] text-slate-500">{j.id}</div></td>
                  <td className="px-4 py-3 text-slate-300">{j.department}</td>
                  <td className="px-4 py-3 text-slate-300">{j.location}</td>
                  <td className="px-4 py-3"><Badge className={`border-none hover:bg-emerald-500/20 ${j.status==='published'?'bg-emerald-500/20 text-emerald-400':'bg-slate-700 text-white'}`}>{j.status}</Badge></td>
                  <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{(j.required_skills||[]).slice(0,3).map(s=><Badge key={s} className="bg-slate-700 text-white border-none hover:bg-slate-700">{s}</Badge>)}</div></td>
                </tr>
              ))}
              {rows.length===0 && <tr><td colSpan={5} className="text-center py-12 text-slate-500"><Briefcase className="w-6 h-6 mx-auto mb-2"/>No jobs yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </AdminLayout>
  );
}
